import type { UIMessage } from "ai";
import { Archive, ExternalLink, Globe2, ImageIcon } from "lucide-react";
import { memo, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";
import type { BrandAssistantSelectedAssetReference } from "@runcheetah/shared";

import {
  getAssistantActivityPartKey,
  getAssistantActivityParts,
  getAssistantCompactionEvent,
  getAssistantMessageDisplayBlocks,
  getAssistantMessageText,
  getAssistantSelectedAssetReferences,
  getAssistantSelectedImageReferences,
  getAssistantKnowledgeBaseQuery,
  getAssistantToolErrorText,
  getAssistantToolInput,
  getAssistantToolLabel,
  getAssistantToolName,
  getAssistantToolOutput,
  getAssistantToolState,
  getAssistantToolStatusLabel,
  getAssistantWebSearchQuery,
  getAssistantWebSearchSourceCount,
  hasRenderableAssistantMessageContent,
  getInternalAssistantHref,
  getSafeAssistantImageSrc,
  parseAssistantSourceReference,
  type AssistantMessagePart,
  type AssistantRuntimeThought,
  type AssistantTraceEntry,
} from "./assistant-chat-helpers";

const MARKDOWN_COMPONENTS = {
  a: AssistantMarkdownLink,
  img: AssistantMarkdownImage,
};

const ASSISTANT_MARKDOWN_CLASS_NAME = [
  "space-y-4 text-[15px] leading-7 text-foreground",
  "[&_a]:font-semibold [&_a]:text-info [&_a]:underline [&_a]:decoration-info/35 [&_a]:underline-offset-4 [&_a:hover]:decoration-info",
  "[&_blockquote]:rounded-2xl [&_blockquote]:border-l-4 [&_blockquote]:border-info/45 [&_blockquote]:bg-info/6 [&_blockquote]:px-4 [&_blockquote]:py-3 [&_blockquote]:text-foreground/78",
  "[&_code]:rounded-md [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em] [&_code]:font-semibold [&_code]:text-foreground",
  "[&_h1]:mt-7 [&_h1]:font-display [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:tracking-[-0.035em] [&_h1]:text-foreground",
  "[&_h2]:mt-6 [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:tracking-[-0.025em] [&_h2]:text-foreground",
  "[&_h3]:mt-5 [&_h3]:font-display [&_h3]:text-base [&_h3]:font-bold [&_h3]:tracking-[-0.01em] [&_h3]:text-foreground",
  "[&_hr]:border-border/80",
  "[&_li]:ml-5 [&_li]:pl-1 [&_li]:marker:text-foreground/45",
  "[&_ol]:space-y-2 [&_ol]:pl-2 [&_ol]:list-decimal",
  "[&_p]:leading-7 [&_p]:text-foreground/88",
  "[&_pre]:overflow-x-auto [&_pre]:rounded-2xl [&_pre]:border [&_pre]:border-border/70 [&_pre]:bg-foreground [&_pre]:p-4 [&_pre]:text-background [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-background",
  "[&_strong]:font-bold [&_strong]:text-foreground",
  "[&_table]:w-full [&_table]:overflow-hidden [&_table]:rounded-2xl [&_table]:border [&_table]:border-border/70 [&_td]:border-t [&_td]:border-border/70 [&_td]:px-3 [&_td]:py-2 [&_th]:bg-muted/70 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold",
  "[&_ul]:space-y-2 [&_ul]:pl-2 [&_ul]:list-disc",
].join(" ");
const ATTACHED_IMAGE_CARD_CLASS =
  "flex w-32 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-muted sm:w-40";

export function AssistantRuntimeStatus({
  isActive,
  thought,
}: {
  isActive: boolean;
  thought: AssistantRuntimeThought | null;
}) {
  if (!thought) {
    return null;
  }

  return (
    <div className="mt-4 pr-8 text-sm leading-6 sm:pr-16" role="status" aria-live="polite">
      <AssistantRuntimeActivity isActive={isActive} thought={thought} />
    </div>
  );
}

export function AssistantRuntimeActivity({
  isActive,
  thought,
}: {
  isActive: boolean;
  thought: AssistantRuntimeThought;
}) {
  const intl = useIntl();

  return (
    <div className="space-y-1 text-muted-foreground">
      <AssistantActivityLogRow
        key={thought.id}
        label={
          <FormattedMessage id="assistantChat.runtimeThinkingLabel" defaultMessage="Thinking" />
        }
        isActive={isActive}
      >
        {intl.formatMessage(thought)}
      </AssistantActivityLogRow>
    </div>
  );
}

function AssistantActivityLogRow({
  label,
  children,
  meta,
  detail,
  isActive = false,
  isError = false,
}: {
  label: ReactNode;
  children: ReactNode;
  meta?: ReactNode;
  detail?: ReactNode;
  isActive?: boolean;
  isError?: boolean;
}) {
  return (
    <div
      className={cn("space-y-1", isActive ? "text-muted-foreground" : "text-muted-foreground/62")}
    >
      <div className="flex min-w-0 flex-wrap items-baseline gap-1.5">
        <span className="text-muted-foreground/40" aria-hidden="true">
          +
        </span>
        <span className="font-medium text-foreground/58">{label}:</span>
        <span className={cn("min-w-0 truncate", isError ? "text-destructive/80" : "text-current")}>
          {isActive ? (
            <span className="bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--muted-foreground)_34%,var(--primary)_50%,var(--muted-foreground)_66%,var(--muted-foreground)_100%)] bg-[length:220%_100%] bg-clip-text text-transparent motion-safe:animate-[assistant-status-aurora_2.2s_linear_infinite]">
              {children}
            </span>
          ) : (
            children
          )}
        </span>
        {meta ? (
          <>
            <span className="text-muted-foreground/42" aria-hidden="true">
              ·
            </span>
            <span className="font-mono text-xs tabular-nums text-muted-foreground/55">{meta}</span>
          </>
        ) : null}
      </div>
      {detail ? (
        <div
          className={cn(
            "truncate pl-4 text-xs leading-5",
            isError ? "text-destructive/75" : "text-muted-foreground/48",
          )}
        >
          {detail}
        </div>
      ) : null}
    </div>
  );
}

export const AssistantChatMessage = memo(function AssistantChatMessage({
  message,
  activityHistory,
  extra,
  assistantExtraPlacement = "afterContent",
  assistantContentClassName,
  assistantExtraClassName,
  renderSelectedAssetReference,
  showActivityParts = true,
  showContent = true,
  getMessageContent,
}: {
  message: UIMessage;
  activityHistory?: ReactNode;
  extra?: ReactNode;
  assistantExtraPlacement?: "beforeContent" | "afterContent";
  assistantContentClassName?: string;
  assistantExtraClassName?: string;
  renderSelectedAssetReference?: (reference: BrandAssistantSelectedAssetReference) => ReactNode;
  showActivityParts?: boolean;
  showContent?: boolean;
  getMessageContent?: (message: UIMessage) => string;
}) {
  const content = showContent
    ? (getMessageContent?.(message) ?? getAssistantMessageText(message))
    : "";
  const selectedAssetReferences = getAssistantSelectedAssetReferences(message);
  const selectedImageReferences = getAssistantSelectedImageReferences(message);
  const compactionEvent = getAssistantCompactionEvent(message);
  const displayBlocks = getAssistantMessageDisplayBlocks({
    message,
    showContent,
    getMessageContent,
  });
  const activityParts = showActivityParts ? getAssistantActivityParts(message) : [];
  const isUser = message.role === "user";
  let renderedInsightTraceCount = 0;

  if (
    !hasRenderableAssistantMessageContent(message, { showActivityParts }) &&
    !activityHistory &&
    !extra
  ) {
    return null;
  }

  return (
    <div className={cn("flex", isUser ? "justify-end" : "w-full justify-start pr-8 sm:pr-16")}>
      <div
        className={cn(
          "min-w-0 text-sm leading-6",
          isUser ? "max-w-[min(84%,36rem)]" : "w-full max-w-[min(100%,52rem)] text-foreground",
        )}
      >
        {isUser ? (
          <div className="flex flex-col items-end gap-2.5">
            {selectedAssetReferences.length > 0 || selectedImageReferences.length > 0 ? (
              <div className="flex flex-wrap justify-end gap-1.5">
                {selectedAssetReferences.map((reference) => (
                  <AssistantSelectedAssetReferenceCard
                    key={reference.generatedAssetId}
                    reference={reference}
                    renderReference={renderSelectedAssetReference}
                  />
                ))}
                {selectedImageReferences.map((reference) => (
                  <AssistantSelectedImageReferenceCard
                    key={reference.generatedAssetId}
                    reference={reference}
                  />
                ))}
              </div>
            ) : null}
            {content ? (
              <div className="max-w-full rounded-3xl bg-primary px-4 py-3 text-primary-foreground shadow-sm">
                <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                  {content}
                </p>
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {compactionEvent ? <AssistantCompactionNotice /> : null}
            {extra && assistantExtraPlacement === "beforeContent" ? (
              <div className={cn(compactionEvent ? "mt-4" : undefined, assistantExtraClassName)}>
                {extra}
              </div>
            ) : null}
            {displayBlocks.length > 0 ? (
              <div
                className={cn(
                  "space-y-4",
                  extra && assistantExtraPlacement === "beforeContent" ? "mt-4" : undefined,
                )}
              >
                {displayBlocks.map((block) => {
                  if (block.type === "content") {
                    return (
                      <AssistantMarkdownContent
                        key={block.key}
                        text={block.text}
                        className={assistantContentClassName}
                      />
                    );
                  }

                  const startingInsightIndex = renderedInsightTraceCount;
                  renderedInsightTraceCount += getAssistantTraceInsightCount(block.entries);

                  return (
                    <AssistantTraceTranscript
                      key={block.key}
                      entries={block.entries}
                      startingInsightIndex={startingInsightIndex}
                    />
                  );
                })}
              </div>
            ) : null}
            {extra && assistantExtraPlacement === "afterContent" ? (
              <div className={cn("mt-4", assistantExtraClassName)}>{extra}</div>
            ) : null}
            {activityHistory ? (
              <div
                className={cn(
                  displayBlocks.length > 0 || extra || compactionEvent ? "mt-4" : undefined,
                )}
              >
                {activityHistory}
              </div>
            ) : null}
            {activityParts.length > 0 ? (
              <div
                className={cn(
                  "space-y-2",
                  displayBlocks.length > 0 || extra || activityHistory || compactionEvent
                    ? "mt-4"
                    : undefined,
                )}
              >
                {activityParts.map((part, index) => (
                  <AssistantActivityPartView
                    key={getAssistantActivityPartKey(part, index)}
                    part={part}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
});

function AssistantMarkdownContent({ text, className }: { text: string; className?: string }) {
  return (
    <div className={cn(ASSISTANT_MARKDOWN_CLASS_NAME, className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

function AssistantTraceTranscript({
  entries,
  startingInsightIndex,
}: {
  entries: readonly AssistantTraceEntry[];
  startingInsightIndex: number;
}) {
  let insightIndex = 0;

  return (
    <div className="max-w-[46rem] space-y-2 text-sm leading-6 text-muted-foreground/76">
      {entries.map((entry) => {
        const isSearch = isAssistantTraceSearchEntry(entry);
        const defaultOpen = isSearch || startingInsightIndex + insightIndex === 0;
        if (!isSearch) {
          insightIndex += 1;
        }

        return <AssistantTraceRow key={entry.id} entry={entry} defaultOpen={defaultOpen} />;
      })}
    </div>
  );
}

function AssistantTraceRow({
  entry,
  defaultOpen,
}: {
  entry: AssistantTraceEntry;
  defaultOpen: boolean;
}) {
  const isSearch = isAssistantTraceSearchEntry(entry);
  const isCollapsible = !isSearch && Boolean(entry.detail);

  if (isCollapsible) {
    return (
      <details className="space-y-0.5" open={defaultOpen}>
        <summary className="cursor-pointer list-none break-words text-foreground/68 [&::-webkit-details-marker]:hidden">
          <AssistantTraceCommand entry={entry} />
        </summary>
        <p className="break-words pl-4">{entry.detail}</p>
      </details>
    );
  }

  return (
    <div className="space-y-0.5">
      <p className="break-words text-foreground/68">
        <AssistantTraceCommand entry={entry} />
      </p>
      {entry.detail ? <p className="break-words pl-4">{entry.detail}</p> : null}
    </div>
  );
}

function AssistantTraceCommand({ entry }: { entry: AssistantTraceEntry }) {
  const isPending = entry.state === "pending";

  return (
    <>
      <span className="text-muted-foreground/45" aria-hidden="true">
        &gt;{" "}
      </span>
      <span
        className={
          isPending
            ? "bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--muted-foreground)_34%,var(--primary)_50%,var(--muted-foreground)_66%,var(--muted-foreground)_100%)] bg-[length:220%_100%] bg-clip-text text-transparent motion-safe:animate-[assistant-status-aurora_2.35s_linear_infinite]"
            : undefined
        }
      >
        {entry.command}
        {isPending ? "..." : ""}
      </span>
    </>
  );
}

function isAssistantTraceSearchEntry(entry: AssistantTraceEntry) {
  return entry.command === "search" || entry.command.startsWith("search ");
}

function getAssistantTraceInsightCount(entries: readonly AssistantTraceEntry[]) {
  return entries.filter((entry) => !isAssistantTraceSearchEntry(entry)).length;
}

function AssistantCompactionNotice() {
  return (
    <div
      className="inline-flex max-w-full items-center gap-2 rounded-full px-1 py-1 text-sm leading-6 text-muted-foreground motion-safe:animate-[assistant-memory-fade-in_260ms_ease-out]"
      role="status"
      aria-live="polite"
    >
      <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground/[0.04] text-foreground/55 ring-1 ring-foreground/8">
        <span
          className="absolute inset-0 rounded-full bg-brand/18 motion-safe:animate-[assistant-memory-pulse_1.9s_ease-in-out_infinite]"
          aria-hidden="true"
        />
        <Archive className="relative h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="bg-[linear-gradient(90deg,var(--foreground)_0%,var(--foreground)_38%,var(--brand)_50%,var(--foreground)_62%,var(--foreground)_100%)] bg-[length:220%_100%] bg-clip-text font-semibold text-transparent motion-safe:animate-[assistant-status-aurora_2.8s_linear_infinite]">
          <FormattedMessage
            id="assistantChat.compactionNotice.title"
            defaultMessage="Memory compressed"
          />
        </span>
        <span className="text-muted-foreground/35" aria-hidden="true">
          ·
        </span>
        <span className="min-w-0 text-muted-foreground/62">
          <FormattedMessage
            id="assistantChat.compactionNotice.body"
            defaultMessage="Earlier context summarized so I can keep the thread moving."
          />
        </span>
      </div>
    </div>
  );
}

function AssistantSelectedAssetReferenceCard({
  reference,
  renderReference,
}: {
  reference: ReturnType<typeof getAssistantSelectedAssetReferences>[number];
  renderReference?: (reference: BrandAssistantSelectedAssetReference) => ReactNode;
}) {
  const safeImageSrc = getSafeAssistantImageSrc(reference.signedUrl);
  const renderedReference = renderReference?.(reference);

  if (renderedReference) {
    return renderedReference;
  }

  return <AttachedImageFrame src={safeImageSrc} aspectRatio="4:5" />;
}

function AssistantSelectedImageReferenceCard({
  reference,
}: {
  reference: ReturnType<typeof getAssistantSelectedImageReferences>[number];
}) {
  const safeImageSrc = getSafeAssistantImageSrc(reference.signedUrl);

  return <AttachedImageFrame src={safeImageSrc} aspectRatio={reference.aspectRatio ?? "4:5"} />;
}

function AttachedImageFrame({ src, aspectRatio }: { src: string | null; aspectRatio: string }) {
  return (
    <div
      className={ATTACHED_IMAGE_CARD_CLASS}
      style={{ aspectRatio: toCssAspectRatio(aspectRatio) }}
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full object-contain" aria-hidden="true" />
      ) : (
        <ImageIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      )}
    </div>
  );
}

function toCssAspectRatio(aspectRatio: string) {
  return aspectRatio.replace(":", " / ");
}

function AssistantActivityPartView({ part }: { part: AssistantMessagePart }) {
  if (part.type === "source-url") {
    return <AssistantSourceUrlPart part={part} />;
  }

  if (part.type === "dynamic-tool" || part.type.startsWith("tool-")) {
    return <AssistantToolPart part={part} />;
  }

  return null;
}

function AssistantToolPart({ part }: { part: AssistantMessagePart }) {
  const intl = useIntl();
  const toolName = getAssistantToolName(part);
  if (!toolName) {
    return null;
  }

  const state = getAssistantToolState(part);
  const isPending = state === "input-streaming" || state === "input-available";
  const isError = state === "output-error";
  const toolLabel = getAssistantToolLabel(toolName, intl);
  const query =
    toolName === "web_search"
      ? (getAssistantWebSearchQuery(getAssistantToolInput(part)) ??
        getAssistantWebSearchQuery(getAssistantToolOutput(part)))
      : toolName === "search_knowledge_base" || toolName === "get_knowledge_base_page"
        ? (getAssistantKnowledgeBaseQuery(getAssistantToolInput(part)) ??
          getAssistantKnowledgeBaseQuery(getAssistantToolOutput(part)))
        : null;
  const sourceCount =
    toolName === "web_search"
      ? getAssistantWebSearchSourceCount(getAssistantToolOutput(part))
      : null;
  const errorText = getAssistantToolErrorText(part);
  const meta =
    sourceCount !== null
      ? intl.formatMessage(
          {
            id: "assistantChat.tool.sourcesCount",
            defaultMessage: "{count, plural, one {# source} other {# sources}}",
          },
          { count: sourceCount },
        )
      : null;
  const detail =
    toolName === "search_knowledge_base" || toolName === "get_knowledge_base_page"
      ? isError
        ? (errorText ?? query)
        : null
      : isError
        ? (errorText ?? query)
        : query;

  return (
    <AssistantActivityLogRow
      label={<FormattedMessage id="assistantChat.toolLabel" defaultMessage="Tool" />}
      meta={meta}
      detail={detail}
      isActive={isPending}
      isError={isError}
    >
      {getAssistantToolStatusLabel({ toolName, toolLabel, state, intl, topic: query })}
    </AssistantActivityLogRow>
  );
}

function AssistantSourceUrlPart({ part }: { part: AssistantMessagePart }) {
  const source = parseAssistantSourceReference(part);
  if (!source) {
    return null;
  }

  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex max-w-full items-center gap-2 rounded-full border border-info/20 bg-info/8 px-3 py-1.5 text-xs text-foreground/72 transition hover:border-info/35 hover:bg-info/12"
    >
      <Globe2 className="h-3.5 w-3.5 shrink-0 text-info" aria-hidden="true" />
      <span className="min-w-0 truncate font-semibold">{source.title ?? source.domain}</span>
      <span className="hidden shrink-0 text-foreground/45 sm:inline">{source.domain}</span>
      <ExternalLink className="h-3 w-3 shrink-0 text-foreground/38" aria-hidden="true" />
    </a>
  );
}

function AssistantMarkdownLink({
  href,
  children,
  node: _node,
  ...props
}: ComponentPropsWithoutRef<"a"> & { node?: unknown }) {
  const internalHref = getInternalAssistantHref(href);

  if (internalHref) {
    return (
      <Link to={internalHref} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} target="_blank" rel="noreferrer" {...props}>
      {children}
    </a>
  );
}

function AssistantMarkdownImage({
  src,
  alt,
  node: _node,
  ...props
}: ComponentPropsWithoutRef<"img"> & { node?: unknown }) {
  const safeSrc = getSafeAssistantImageSrc(src);
  if (!safeSrc) {
    return null;
  }

  return (
    <span className="my-4 block overflow-hidden rounded-3xl border border-border/80 bg-card shadow-[0_18px_45px_-32px_rgba(19,34,49,0.45)]">
      <img
        {...props}
        src={safeSrc}
        alt={alt ?? ""}
        loading="lazy"
        decoding="async"
        className="max-h-[28rem] w-full object-contain"
      />
      {alt ? <span className="block px-4 py-2 text-xs text-foreground/58">{alt}</span> : null}
    </span>
  );
}
